import {
  MAX_LONG_TEXT_ANSWER_CHARS,
  MAX_SHORT_TEXT_ANSWER_CHARS,
  type AppointmentTypeDto,
  type BookingQuestion,
  type BookingQuestionType,
} from '@shared/core/appointment-types'
import {
  bookingTextLineModeFor,
  countCodePoints,
  sanitizeBookingText,
  type BookingAnswerValue,
  type CreateBookingDto,
} from '@shared/core/bookings'
import { formatInTimeZone } from 'date-fns-tz'
import parsePhoneNumberFromString from 'libphonenumber-js'
import type { BookingAnswerMap, PinnedBookingForm } from 'src/modules/booking/types/booking-form.types'
import {
  CUSTOMER_ADDRESS_REQUIRED_ERROR,
  QUESTION_PICK_OPTION_ERROR,
  QUESTION_REQUIRED_ERROR,
} from './booking-details-step.constants'
import type { AppointmentTypeForLabel, SlotForValidation } from './booking-details-step.interface'

/** Mirror `create-booking-request.dto.ts`; the server is the authority. */
export const MAX_EMAIL_LENGTH = 320
export const MAX_FULL_NAME_LENGTH = 200
export const MAX_CUSTOMER_ADDRESS_LENGTH = 500
const EMAIL_LOCAL_CHAR = "[\\w!#$%&'*+/=?^`{|}~\\u00A1-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFFEF-]"
const EMAIL_LABEL_CHAR = '[a-z0-9\\u00A1-\\uFF00]'
const EMAIL_TLD = '(?:[a-z\\u00A1-\\u00A8\\u00AA-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFF00]{2,}|xn--[a-z0-9-]+)'
// Never looser than the server's `@IsEmail` (validator.js); only quoted local parts are stricter.
const EMAIL_PATTERN = new RegExp(
  `^${EMAIL_LOCAL_CHAR}+(?:\\.${EMAIL_LOCAL_CHAR}+)*` +
    `@(?:${EMAIL_LABEL_CHAR}(?:[a-z0-9\\u00A1-\\uFF00-]*${EMAIL_LABEL_CHAR})?\\.)+${EMAIL_TLD}$`,
  'i',
)

export function isValidFullName(name: string): boolean {
  return name.trim().length >= 5 && name.length <= MAX_FULL_NAME_LENGTH
}

export function isValidEmail(email: string): boolean {
  const trimmed = (email ?? '').trim()
  if (trimmed.length === 0) return false
  if (trimmed.length > MAX_EMAIL_LENGTH) return false
  return EMAIL_PATTERN.test(trimmed)
}

export function isValidPhoneNumber(phone: string): boolean {
  if (!phone || phone.trim() === '') return false
  try {
    const parsed = parsePhoneNumberFromString(phone)
    return parsed?.isValid() ?? false
  } catch {
    return false
  }
}

export function getFullNameValidationError(name: string): string | null {
  if (name.length > MAX_FULL_NAME_LENGTH) return `Full name cannot exceed ${MAX_FULL_NAME_LENGTH} characters`
  return isValidFullName(name) ? null : 'Full name must be at least 5 characters'
}

export function getEmailValidationError(email: string): string | null {
  if ((email ?? '').trim().length === 0) return 'Please enter your email address.'
  return isValidEmail(email) ? null : 'Please enter a valid email address.'
}

export function getPhoneNumberValidationError(phone: string): string | null {
  return isValidPhoneNumber(phone) ? null : 'Please enter a valid phone number'
}

export function requiresCustomerAddress(appointmentType?: Pick<AppointmentTypeDto, 'location_type'> | null): boolean {
  return appointmentType?.location_type === 'customer_address'
}

export function getCustomerAddressValidationError(address: string): string | null {
  return address.trim().length > 0 ? null : CUSTOMER_ADDRESS_REQUIRED_ERROR
}

export function getAppointmentTypeLabel(
  appointmentTypes: AppointmentTypeForLabel[],
  appointmentTypeId?: string,
): string {
  if (!appointmentTypeId || appointmentTypes.length === 0) return ''
  const type = appointmentTypes.find((t) => t.id === appointmentTypeId)
  if (!type) return ''
  return `${type.name} ${type.duration_minutes} min`
}

export function computeEndTimeDisplay(selectedTime: string, durationMinutes: number): string {
  const [time, period] = selectedTime.split(' ')
  const [hours, minutes] = time.split(':').map(Number)
  let nextHours = hours
  let nextMinutes = minutes + durationMinutes
  if (nextMinutes >= 60) {
    nextHours += Math.floor(nextMinutes / 60)
    nextMinutes = nextMinutes % 60
  }
  return `${nextHours}:${nextMinutes.toString().padStart(2, '0')} ${period}`
}

export function findSlotByDisplayTime(
  slots: SlotForValidation[],
  selectedTime: string,
  timezone: string,
): SlotForValidation | null {
  if (!slots.length || !selectedTime) return null
  const tz = timezone || 'UTC'
  return (
    slots.find((slot) => {
      const startTime = new Date(slot.start_time_utc)
      const formatted = formatInTimeZone(startTime, tz, 'h:mm a')
      return formatted === selectedTime
    }) ?? null
  )
}

const ANSWER_CHAR_LIMITS: Partial<Record<BookingQuestionType, number>> = {
  short_text: MAX_SHORT_TEXT_ANSWER_CHARS,
  long_text: MAX_LONG_TEXT_ANSWER_CHARS,
}

/** The cap for a text question in code points, as the server counts (design D40); null for choice types. */
export function getAnswerCharLimit(type: BookingQuestionType): number | null {
  return ANSWER_CHAR_LIMITS[type] ?? null
}

export function getBookingQuestionError(
  question: BookingQuestion,
  value: BookingAnswerValue | undefined,
): string | null {
  const limit = getAnswerCharLimit(question.type)
  if (limit !== null) return getTextAnswerError(question, value, limit)
  if (!question.required || hasChoiceAnswer(value)) return null
  return question.type === 'checkboxes' ? QUESTION_REQUIRED_ERROR : QUESTION_PICK_OPTION_ERROR
}

export function formatMaxCharsError(limit: number): string {
  return `Maximum ${limit.toLocaleString('en-US')} characters.`
}

export function limitToCodePoints(text: string, limit: number): string {
  const codePoints = Array.from(text)
  return codePoints.length > limit ? codePoints.slice(0, limit).join('') : text
}

/** A revision-0 type has no served form, so it books through the legacy payload (design D28). */
export function buildBookingFormPayload(
  form: PinnedBookingForm | undefined,
  answers: BookingAnswerMap,
): Pick<CreateBookingDto, 'booking_form_revision' | 'answers'> {
  if (!form || form.revision < 1) return {}
  return {
    booking_form_revision: form.revision,
    answers: form.questions.map((question) => ({
      question_id: question.id,
      value: toSubmittedValue(question, answers[question.id]),
    })),
  }
}

function getTextAnswerError(question: BookingQuestion, value: BookingAnswerValue | undefined, limit: number) {
  const text = normalizeTextAnswer(question.type, value)
  if (!text) return question.required ? QUESTION_REQUIRED_ERROR : null
  return countCodePoints(text) > limit ? formatMaxCharsError(limit) : null
}

function normalizeTextAnswer(type: BookingQuestionType, value: BookingAnswerValue | undefined): string {
  return typeof value === 'string' ? sanitizeBookingText(value, bookingTextLineModeFor(type)) : ''
}

function hasChoiceAnswer(value: BookingAnswerValue | undefined): boolean {
  return Array.isArray(value) ? value.length > 0 : !!value
}

function toSubmittedValue(question: BookingQuestion, value: BookingAnswerValue | undefined): BookingAnswerValue {
  if (getAnswerCharLimit(question.type) !== null) return normalizeTextAnswer(question.type, value) || null
  return hasChoiceAnswer(value) ? (value as BookingAnswerValue) : null
}
